import * as styles from './CardImage.module.css'

import React, { useCallback, useMemo, useState } from 'react'
import classNames from 'classnames'

import * as scryfall from 'utils/scryfall'

import { useIsTouchDevice } from 'utils/useIsTouchDevice'

import FlipButton from './FlipButton'

interface Props {
  children: string
  set?: string
  id?: string
  imageURL?: string
  canFlip?: boolean
  flipMode?: 'hover' | 'button'
  imageClass?: string
  emphasized?: boolean
}

const CardImage: React.FC<Props> = (props) => {
  const {
    children,
    set,
    id,
    imageURL,
    canFlip = false,
    flipMode = 'hover',
    imageClass,
    emphasized = false,
  } = props

  const [isFlipped, setFlipped] = useState(false)

  const [frontFaceFailed, setFrontFaceFailed] = useState(false)
  const [backFaceFailed, setBackFaceFailed] = useState(false)

  const imageURLs = useMemo(() => {
    return imageURL != null
      ? { front: imageURL }
      : scryfall.imageURLs({ cardName: children, set, id })
  }, [children, id, imageURL, set])

  const isDoubleFaced = imageURLs.back != null && !backFaceFailed

  const isTouchDevice = useIsTouchDevice()

  const mouseHandlers = useMemo(() => {
    if (!canFlip || !isDoubleFaced || flipMode === 'button') {
      return {}
    }
    if (isTouchDevice) {
      return {
        onClick: () => isDoubleFaced && setFlipped((f) => !f),
      }
    }
    return {
      onMouseEnter: () => isDoubleFaced && setFlipped(true),
      onMouseLeave: () => isDoubleFaced && setFlipped(false),
    }
  }, [canFlip, flipMode, isDoubleFaced, isTouchDevice])

  const flip = useCallback((event: React.MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setFlipped((isFlipped) => !isFlipped)
  }, [])

  return (
    <div
      // This key forces a re-render rather than having the image source
      // attribute changed to prevent stale images.
      key={children}
      className={classNames([styles.container], {
        [styles.flipped]: isFlipped && canFlip,
        [styles.emphasized]: emphasized,
      })}
      {...mouseHandlers}
    >
      <div className={styles.card}>
        <img
          loading="lazy"
          className={classNames(styles.face, imageClass)}
          src={frontFaceFailed ? '/mtg-card-back.webp' : imageURLs.front}
          width={215}
          height={300}
          alt={children}
          draggable={false}
          onError={() => setFrontFaceFailed(true)}
        />
        {imageURLs.back && (
          <img
            loading="lazy"
            className={classNames(styles.back, imageClass)}
            src={imageURLs.back}
            width={215}
            height={300}
            alt=""
            draggable={false}
            onError={() => setBackFaceFailed(true)}
          />
        )}
      </div>

      {isDoubleFaced && flipMode === 'button' && (
        <FlipButton isFlipped={isFlipped && canFlip} onCLick={flip} />
      )}
    </div>
  )
}

export default CardImage
